import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Nav, INavStyles, INavLinkGroup, INavLink } from '@fluentui/react/lib/Nav';
import { Text, Stack, IStackStyles } from '@fluentui/react';
import { User } from '../../system/User';
import { APP_VERSION, DISABLE_MMI_OPTION, LOGO_PATROCINADOR, routes } from '../../common/constants';
import { t } from 'i18next';
import { isMobileDevice } from '../../common/isMobileDevice';
import { ClientConfigName, useLiquidacionAgrupadaConfig } from '../../shared/hooks/useLiquidacionAgrupadaConfig';

const KEY_HOME = 'Home';
const KEY_TUTORIAL = 'Tutorial';
const KEY_CALENDAR = 'Calendar';
const KEY_PROJECTS = 'Projects';
const KEY_ACTIVITIES = 'Activities';
const KEY_COMPANYTREE = 'CompanyTree';
const KEY_DOCUMENTS = 'Documents';
const KEY_MAILS = 'Mails';
const KEY_PANEL = 'Panel';
const KEY_REPORTING = 'Reporting';
const KEY_REPORTING_COMPANY = 'ReportingCompany';
const KEY_REPORTING_SECTOR = 'ReportingSector';
const KEY_REPORTING_PROJECT = 'ReportingProject';
const KEY_REPORTING_BILLABLE_DISTRIBUTION = 'ReportingBillableDistribution';
const KEY_REPORTING_DOCS_PROJECT = 'ReportingDocsProject';
const KEY_REPORTING_PRELIQUIDATION = 'ReportingPreliquidation';
const KEY_DIRECT_LIQUIDATION = 'DirectLiquidation';
const KEY_DIRECT_LIQUIDATION_AJE = 'DirectLiquidationAje';
const KEY_SETTLEMENTS = 'Settlements';
const KEY_SETTLEMENTS_AJE = 'SettlementsAje';
const KEY_LOADS = 'loads';

const KEY_TEMPLATES = 'CostPerHour';
const KEY_TEMPLATES_AJE = 'CostPerHourAje';

const KEY_LISTADO = 'Listado';

const KEY_COLLABORATORS = 'Collaborators';
const KEY_DEFENSEFILES = 'DefenseFiles';
const KEY_DEFENSEFILE = 'DefenseFile';
const KEY_MASSIVEDEFENSE = 'MassiveDefense';
const KEY_ADMIN = 'Admin';
const KEY_IMPORT_ACTIVITIES_EXCEL = 'ImportActivitiesExcel';
const KEY_LICENSES = 'Licenses';
const KEY_PERIODS_BLOCKED = 'PeriodsBlock';
const KEY_CARGAR_POR_FORMULARIO = 'Cargarporformulario';
const kEY_CONFIGURACIONES = 'Configuraciones';
const KEY_REPORTING_ACTIVIDADES = 'ActivityReport';
const KEY_SALE = 'ventas';
const KEY_COST_CENTER = 'cost-center';
const KEY_REPORTS_QUANTIFIED_HOURS = 'ReportingQuantifiedHours';


interface AppNavProps {
  onLinkClicked?: () => void;
  className?: string;
  styles?: Partial<INavStyles>;
  user: User;
  menudata:INavLinkGroup[];
}

const optionsAvailableForStandardUser = [
  KEY_HOME,
  KEY_TUTORIAL,
  KEY_CALENDAR,
  KEY_PROJECTS,
  KEY_ACTIVITIES,
  KEY_PANEL,
  KEY_IMPORT_ACTIVITIES_EXCEL,
  KEY_LICENSES,
  KEY_CARGAR_POR_FORMULARIO,
];


const optionsAvailableForLiquidatorUser = [
  ...optionsAvailableForStandardUser,
  KEY_SALE,
  KEY_LOADS,
  KEY_SETTLEMENTS_AJE,
]

// solo a talento humano se le va a mostrar el menu de costo por hora de aje
const optionsAvailableForTalHum = [
  ...optionsAvailableForStandardUser,
  KEY_TEMPLATES_AJE
];

const optionsAvailableForDirective = [
  ...optionsAvailableForStandardUser,
  KEY_DOCUMENTS,
  KEY_REPORTING,
  KEY_REPORTING_COMPANY,
  KEY_REPORTING_SECTOR,
  KEY_REPORTING_PRELIQUIDATION,
  KEY_COLLABORATORS,
  KEY_COMPANYTREE,
];

const optionsAvailableForPowerUser = [
  ...optionsAvailableForDirective,
  kEY_CONFIGURACIONES,
  KEY_MAILS,
  KEY_SETTLEMENTS,
  KEY_DEFENSEFILES,
  KEY_ADMIN,
  KEY_PERIODS_BLOCKED,
  KEY_TEMPLATES,
  KEY_REPORTING_ACTIVIDADES,
  KEY_COST_CENTER,
  KEY_REPORTS_QUANTIFIED_HOURS,
];

const AppNav: React.FC<AppNavProps> = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedKeys, setExpandedKeys] = React.useState<string[]>([]);

  const handleLinkClick = (ev?: React.MouseEvent<HTMLElement>, item?: INavLink) => {
    ev?.preventDefault();
    if (!item) return;

    // Si tiene submenús, alternamos expansión
    if (item.links && item.links.length > 0) {
      setExpandedKeys((prev) =>
        prev.includes(item.key!)
          ? prev.filter((k) => k !== item.key)
          : [...prev, item.key!]
      );
    } else {
      // Navegar normalmente
      if (item.url) navigate(item.url);
      if (props.onLinkClicked) props.onLinkClicked();
    }
  };

  
  // Manejo de clic en el ícono desplegable (chevron)
  const handleExpandClick = (ev?: React.MouseEvent<HTMLElement>, item?: INavLink) => {
    ev?.preventDefault();
    if (!item) return;

    // Alternar el estado
    setExpandedKeys((prev) =>
      prev.includes(item.key!)
        ? prev.filter((k) => k !== item.key)
        : [...prev, item.key!]
    );
  };

  const filterOptionsByUser = () => {
    let optionsForUser: string[] = [];
    const isMobile = isMobileDevice();

    if (props.user.getProfile().isStandardUser()) optionsForUser = optionsAvailableForStandardUser;
    if (props.user.getProfile().isDirectiveUser()) optionsForUser = optionsAvailableForDirective;
    if (props.user.getProfile().isPowerUser()) optionsForUser = optionsAvailableForPowerUser;
    if (props.user.getProfile().isAdminUser()) optionsForUser = optionsAvailableForPowerUser;
    if (props.user.getProfile().isTalHumUser()) optionsForUser = optionsAvailableForTalHum;
    if (props.user.getProfile().isLiquidatorUser()) optionsForUser = optionsAvailableForLiquidatorUser;

    /* const configLiquidacionAgrupada = props.user.getClientConfigs().find((c) => c.cltconf_perid === props.user.getProfile().getId() && c.cltconf_nombre === ClientConfigName.OPC_LIQUIDACION_AGRUPADA)
    const hasLiquidacionAgrupada = Boolean(configLiquidacionAgrupada);
    // REGLA ESPECIAL PARA KEY_TEMPLATES
    if (hasLiquidacionAgrupada) {
      if (props.user.getProfile().isTalHumUser()) {
        // Talento Humano sí puede ver Templates → no hacemos nada
      } else {
        // Power/Admin/otros → quitar Templates
        optionsForUser = optionsForUser.filter(
          key => key !== KEY_TEMPLATES
        );
      }
    } */


    let navLinkGroupsByUser: INavLinkGroup[] = [
      {
        links: [
        ],
      },
    ];

    props.menudata[0]?.links.forEach((link) => {
      if (!optionsForUser.includes(link.key!)) return;

      let newLink = { ...link };

      if (link.links && link.links.length > 0) {
        newLink.links = link.links.filter((sublink) => {
          // Mostrar solo uno según el dispositivo
          if (isMobile && sublink.key === "cargatabla") return false; // ocultar tabla en móvil
          if (!isMobile && sublink.key === "Cargaformulario") return false; // ocultar formulario en escritorio
          // Si no tiene permisos evitar mostrarle el enlace en este caso para reportes de actividades pero se podria colocar para toda las rutas
          if (!optionsForUser.includes(sublink.key!) && sublink.key === KEY_REPORTING_ACTIVIDADES) return false
          if (!optionsForUser.includes(sublink.key!) && sublink.key === KEY_COST_CENTER) return false
          if (!optionsForUser.includes(sublink.key!) && sublink.key === KEY_REPORTS_QUANTIFIED_HOURS) return false

          return true;
        });

        // Solo agregar el menú padre si tiene sublinks visibles
        if (newLink.links.length > 0) {
          navLinkGroupsByUser[0].links.push(newLink);
        }
      } else {
        navLinkGroupsByUser[0].links.push(newLink);
      }
    });

    return navLinkGroupsByUser;
  };

  const optionsSidebar = React.useMemo(() => {
    if (props.user) {
      return filterOptionsByUser();
    }
    return [];
  }, [props.user]);

  const selectedKey = React.useMemo(() => {
    const normalizePath = (path: string) => path.replace(/^\/+|\/+$/g, "");
    const locationPath = normalizePath(location.pathname);

    let foundKey: string | undefined;
    let parentKeys: string[] = []; // guardamos los padres a expandir

    const searchLinks = (links: INavLink[] = [], parents: string[] = []): boolean => {
      for (const link of links) {
        const linkUrl = link.url ? normalizePath(link.url) : "";

        // Si coincide la URL, guardamos el key y sus padres
        if (linkUrl && locationPath.startsWith(linkUrl)) {
          foundKey = link.key;
          parentKeys = parents;
          return true;
        }

        // Si tiene hijos, los recorremos y pasamos los padres acumulados
        if (link.links && searchLinks(link.links, [...parents, link.key!])) {
          return true;
        }
      }
      return false;
    };

    for (const group of optionsSidebar) {
      if (searchLinks(group.links)) break;
    }

    // expandimos automáticamente los padres
    if (parentKeys.length > 0) {
      setExpandedKeys(prev => Array.from(new Set([...prev, ...parentKeys])));
    }

    return foundKey;
  }, [location.pathname, optionsSidebar]);

  const stackStyles: IStackStyles = {
    root: {
      height: '100%',
    },
  };

  const processedMenu = optionsSidebar.map(group => ({
    ...group,
    links: group.links.map(link_1 => ({
      ...link_1,
      isExpanded: expandedKeys.includes(link_1.key!),
      iconProps: {
        className: link_1.links && link_1.links.length > 0 ? 'opacity-0' : link_1.iconProps?.className,
        iconName: link_1.links && link_1.links.length > 0 ? 'ChevronDown' : link_1.icon,
      },
      links: link_1.links?.map(link_2 => ({
        ...link_2,
        isExpanded: expandedKeys.includes(link_2.key!),
        iconProps: {
          className: link_2.links && link_2.links.length > 0 ? 'opacity-0' : link_2.iconProps?.className,
          iconName: link_2.links && link_2.links.length > 0 ? 'ChevronDown' : link_2.icon,
        },
        links: link_2.links?.map(link_3 => ({
          ...link_3,
          isExpanded: expandedKeys.includes(link_3.key!),
          iconProps: {
            className: link_3.links && link_3.links.length > 0 ? 'opacity-0' : link_3.iconProps?.className,
            iconName: link_3.links && link_3.links.length > 0 ? 'ChevronDown' : link_3.icon,
          },
          links: link_3.links?.map(link_4 => ({
            ...link_4,
            isExpanded: expandedKeys.includes(link_4.key!),
            iconProps: {
              className: link_4.links && link_4.links.length > 0 ? 'opacity-0' : link_4.iconProps?.className,
              iconName: link_4.links && link_4.links.length > 0 ? 'ChevronDown' : link_4.icon,
            }
          }))
        })),
      }))
    })),
  }));

  return (
        <Stack verticalFill={true} className='nav-stack'>
          <Stack.Item grow={3} styles={stackStyles} className={'nav-container'}>
            <Nav
                selectedKey={selectedKey}
                // ariaLabel="Barra de navegación"
                // styles={props.styles}
                groups={processedMenu}
                onLinkClick={handleLinkClick}
                onLinkExpandClick={handleExpandClick}
                className={props.className}
            />
          </Stack.Item>
        {/* <Stack.Item grow={1} style={{ textAlign: 'center', paddingTop: '10px' }} className={'hide-sidebar-info collab-info-title'}>
        <Text className="mt-2" variant="small">
          {t('En colaboración con')}
        </Text>
      </Stack.Item> */}
        {LOGO_PATROCINADOR ? (
            <Stack.Item grow={1} style={{ textAlign: 'center' }} className={'collab-info'}>
              <img
                  className="collab-logo mt-1 mb-1"
                  src={LOGO_PATROCINADOR}
                  alt="logo"
                  title={t('En colaboración con')}
              />
            </Stack.Item>
        ) : null}
        <Stack.Item style={{ textAlign: 'center' }} className={'hide-sidebar-info copyright-info'}>
          <Text className="mt-1" variant="small" title={`Version ${APP_VERSION}`}>
            {t(`Copyright @${new Date().getFullYear()}`)} &nbsp;
          </Text>
        </Stack.Item>
        {/* <Stack.Item style={{ textAlign: 'center' }} className={'version-info'}>
        <Text className="mt-2" variant="small">
          {APP_VERSION}
        </Text>
      </Stack.Item> */}
      </Stack>
  );
};

export default AppNav;
